import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { SettingsLayout } from '@/components/layout/SettingsLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { RestrictedRoute } from './RestrictedRoute';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { AdminRoute } from './AdminRoute';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { DesignerPermissionGate } from '@/features/designer/components/DesignerPermissionGate';
import { lazyWithRetry } from '@/lib/lazy-with-retry';

// Auth pages (lazy loaded — retries once on chunk load failure after deployments)
const LoginPage = lazyWithRetry(() => import('@/features/auth/pages/LoginPage'));
const SignupPage = lazyWithRetry(() => import('@/features/auth/pages/SignupPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/features/auth/pages/ForgotPasswordPage'));
const MfaPage = lazyWithRetry(() => import('@/features/auth/pages/MfaPage'));
const SamlLoginPage = lazyWithRetry(() => import('@/features/auth/pages/SamlLoginPage'));
const SamlAppLoginPage = lazyWithRetry(() => import('@/features/auth/pages/SamlAppLoginPage'));
const SsoLoginPage = lazyWithRetry(() => import('@/features/auth/pages/SsoLoginPage'));
const PasswordResetPage = lazyWithRetry(() => import('@/features/auth/pages/PasswordResetPage'));
const VerifyEmailPage = lazyWithRetry(() => import('@/features/auth/pages/VerifyEmailPage'));
const MagicLinkSignInPage = lazyWithRetry(
  () => import('@/features/auth/pages/MagicLinkSignInPage'),
);
const JoinTeamPage = lazyWithRetry(() => import('@/features/auth/pages/JoinTeamPage'));

// Feature pages (lazy loaded, behind feature flags)
const ScreensPage = lazyWithRetry(() => import('@/features/screens/pages/ScreensPage'));
const useNativeScreens = import.meta.env.VITE_USE_NATIVE_SCREENS === 'true';

const AssetsPage = lazyWithRetry(() => import('@/features/assets/pages/AssetsPage'));
const useNativeAssets = import.meta.env.VITE_USE_NATIVE_ASSETS === 'true';

const PlaylistsPage = lazyWithRetry(() => import('@/features/playlists/pages/PlaylistsPage'));
const useNativePlaylists = import.meta.env.VITE_USE_NATIVE_PLAYLISTS === 'true';

const SchedulesPage = lazyWithRetry(() => import('@/features/schedules/pages/SchedulesPage'));
const useNativeSchedules = import.meta.env.VITE_USE_NATIVE_SCHEDULES === 'true';

const PersonalProfilePage = lazyWithRetry(
  () => import('@/features/settings/pages/PersonalProfilePage'),
);
const useNativeProfile = import.meta.env.VITE_USE_NATIVE_PROFILE === 'true';

const CompanyProfilePage = lazyWithRetry(
  () => import('@/features/settings/pages/CompanyProfilePage'),
);
const useNativeCompanyProfile = import.meta.env.VITE_USE_NATIVE_COMPANY_PROFILE === 'true';

const ExternalComsPage = lazyWithRetry(
  () => import('@/features/external-coms/pages/ExternalComsPage'),
);
const useNativeExternalComs = import.meta.env.VITE_USE_NATIVE_EXTERNAL_COMS === 'true';

const PreferencesPage = lazyWithRetry(() => import('@/features/settings/pages/PreferencesPage'));
const useNativePreferences = import.meta.env.VITE_USE_NATIVE_PREFERENCES === 'true';

const ApiKeysPage = lazyWithRetry(() => import('@/features/settings/pages/ApiKeysPage'));
const useNativeApiKeys = import.meta.env.VITE_USE_NATIVE_APIKEYS === 'true';

const OperationalSchedulePage = lazyWithRetry(
  () => import('@/features/settings/pages/OperationalSchedulePage'),
);
const useNativeOpsSchedule = import.meta.env.VITE_USE_NATIVE_OPS_SCHEDULE === 'true';

const MonitoringAlertRulesPage = lazyWithRetry(
  () => import('@/features/monitoring-alert-rules/pages/MonitoringAlertRulesPage'),
);
const useNativeMonitoringAlertRules =
  import.meta.env.VITE_USE_NATIVE_MONITORING_ALERT_RULES === 'true';

const ProofOfPlayPage = lazyWithRetry(() => import('@/features/analytics/pages/ProofOfPlayPage'));

const ProofOfPlaySettingsPage = lazyWithRetry(
  () => import('@/features/proof-of-play-settings/pages/ProofOfPlaySettingsPage'),
);
const useNativePopSettings = import.meta.env.VITE_USE_NATIVE_POP_SETTINGS === 'true';
const AiAnalyticsPage = lazyWithRetry(() => import('@/features/analytics/pages/AiAnalyticsPage'));

const EngagePage = lazyWithRetry(() => import('@/features/engage/pages/EngagePage'));
const EngageHubPage = lazyWithRetry(() => import('@/features/engage/pages/EngageHubPage'));
const useNativeEngage = import.meta.env.VITE_USE_NATIVE_ENGAGE === 'true';

const FeedsPageLazy = lazyWithRetry(() =>
  import('@/features/feeds/pages/FeedsPage').then((m) => ({
    default: () => <m.default mode="app" />,
  })),
);
const FeedsPagePublicLazy = lazyWithRetry(() =>
  import('@/features/feeds/pages/FeedsPage').then((m) => ({
    default: () => <m.default mode="public" />,
  })),
);
const useNativeFeeds = import.meta.env.VITE_USE_NATIVE_FEEDS === 'true';
const useNativeFeedsAdmin = import.meta.env.VITE_USE_NATIVE_FEEDS_ADMIN === 'true';
const usePublicFeedsSite = import.meta.env.VITE_USE_PUBLIC_FEEDS_SITE === 'true';

const FeedsAdminPageLazy = lazyWithRetry(() => import('@/features/feeds/pages/FeedsAdminPage'));

const TemplatesPage = lazyWithRetry(() => import('@/features/templates/pages/TemplatesPage'));
const useNativeTemplates = import.meta.env.VITE_USE_NATIVE_TEMPLATES === 'true';

const DesignerPage = lazyWithRetry(() => import('@/features/designer/pages/DesignerPage'));
const useNativeDesigner = import.meta.env.VITE_USE_NATIVE_DESIGNER === 'true';
const DesignEditorPage = lazyWithRetry(() => import('@/features/designer/pages/DesignEditorPage'));
const AuditLogPage = lazyWithRetry(() => import('@/features/audit-logs/pages/AuditLogPage'));
const useNativeAuditLog = import.meta.env.VITE_USE_NATIVE_AUDIT_LOG === 'true';

const DataSourcesPage = lazyWithRetry(() => import('@/features/datasources/pages/DataSourcesPage'));
const useNativeDataSources = import.meta.env.VITE_USE_NATIVE_DATASOURCES === 'true';

const IntegrationsPage = lazyWithRetry(
  () => import('@/features/integrations/pages/IntegrationsPage'),
);
const useNativeIntegrations = import.meta.env.VITE_USE_NATIVE_INTEGRATIONS === 'true';

const ShareLinksAdminPage = lazyWithRetry(
  () => import('@/features/share-links/pages/ShareLinksAdminPage'),
);
const useNativeShareLinksAdmin = import.meta.env.VITE_USE_NATIVE_SHARE_LINKS_ADMIN === 'true';

// Share link pages (standalone, no AppShell — accessible without login)
const SignInSharePage = lazyWithRetry(() => import('@/features/share-links/pages/SignInSharePage'));
const PlaylistSharePage = lazyWithRetry(
  () => import('@/features/share-links/pages/PlaylistSharePage'),
);
const FolderSharePage = lazyWithRetry(() => import('@/features/share-links/pages/FolderSharePage'));

const OptiDevPage = lazyWithRetry(() => import('@/features/optidev/pages/OptiDevPage'));
const ManifestPlaygroundPage = lazyWithRetry(
  () => import('@/features/smallapp/pages/ManifestPlaygroundPage'),
);

// Local-only dev routes (not committed — see .git/info/exclude)
const devRouteModules = import.meta.env.DEV
  ? import.meta.glob('./dev-routes.tsx', { eager: true })
  : {};
const devMod = devRouteModules['./dev-routes.tsx'] as
  | {
      devRootRoutes?: import('react-router').RouteObject[];
      devAppRoutes?: import('react-router').RouteObject[];
    }
  | undefined;

function LazyWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    children: [
      // === Public feeds site — only when VITE_USE_PUBLIC_FEEDS_SITE=true (separate deployment) ===
      // Root URL `/` renders full-screen feeds page without AppShell or auth.
      // "Next" redirects to the portal deployment (VITE_PORTAL_URL) for auth + asset creation.
      ...(usePublicFeedsSite
        ? [
            {
              index: true,
              element: (
                <LazyWrapper>
                  <FeedsPagePublicLazy />
                </LazyWrapper>
              ),
            },
          ]
        : []),

      ...(devMod?.devRootRoutes ?? []),

      // === Public Design Editor — standalone, no AppShell ===
      // Matches scio1 /designEditor and scio2 /design-editor.
      // Redirects authenticated users to /app/designManagement.
      {
        path: 'designEditor',
        element: (
          <LazyWrapper>
            <DesignEditorPage />
          </LazyWrapper>
        ),
      },
      // scio2 kebab-case alias
      {
        path: 'design-editor',
        element: (
          <LazyWrapper>
            <DesignEditorPage />
          </LazyWrapper>
        ),
      },

      // === Share Link pages — standalone, no AppShell, no auth required ===
      {
        path: 'share/signIn',
        element: (
          <LazyWrapper>
            <SignInSharePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'share/playlist/:objectId',
        element: (
          <LazyWrapper>
            <PlaylistSharePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'share/folder/:objectId',
        element: (
          <LazyWrapper>
            <FolderSharePage />
          </LazyWrapper>
        ),
      },

      // === AppShell layout — wraps all portal routes (auth, app, settings) ===
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          // Root redirect (only active when NOT public feeds site, since public feeds takes the index above)
          ...(!usePublicFeedsSite
            ? [{ index: true, element: <Navigate to="/app/screenManagement" replace /> }]
            : []),

          // === Auth routes (match scio1 exactly) ===
          // Sign in routes
          {
            path: 'signIn',
            element: (
              <LazyWrapper>
                <LoginPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'signIn/:accountId',
            element: (
              <LazyWrapper>
                <LoginPage />
              </LazyWrapper>
            ),
          },
          // MFA (scio1: /signIn/mfa)
          {
            path: 'signIn/mfa',
            element: (
              <LazyWrapper>
                <MfaPage />
              </LazyWrapper>
            ),
          },
          // SAML (scio1: /signIn/saml)
          {
            path: 'signIn/saml',
            element: (
              <LazyWrapper>
                <SamlLoginPage />
              </LazyWrapper>
            ),
          },
          // SAML App — Flutter mobile app (scio1: /signIn/samlApp)
          {
            path: 'signIn/samlApp',
            element: (
              <LazyWrapper>
                <SamlAppLoginPage />
              </LazyWrapper>
            ),
          },

          // SAML callback from backend ACS redirect (open-api redirects to /sign-in/saml?token={tokenId})
          {
            path: 'sign-in/saml',
            element: (
              <LazyWrapper>
                <SamlLoginPage />
              </LazyWrapper>
            ),
          },
          // Legacy: keep /sign-in for backwards compatibility with older backend versions
          {
            path: 'sign-in',
            element: (
              <LazyWrapper>
                <SamlLoginPage />
              </LazyWrapper>
            ),
          },

          // Sign up routes (direct - match scio1 for marketing site compatibility)
          {
            path: 'signUp',
            element: (
              <LazyWrapper>
                <SignupPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'signUp/:planType',
            element: (
              <LazyWrapper>
                <SignupPage />
              </LazyWrapper>
            ),
          },

          // === Password routes (match scio1 exactly) ===
          // Forgot password (scio1: /password/forgot)
          {
            path: 'password/forgot',
            element: (
              <LazyWrapper>
                <ForgotPasswordPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'password/forgot/:accountId',
            element: (
              <LazyWrapper>
                <ForgotPasswordPage />
              </LazyWrapper>
            ),
          },
          // Password reset - Firebase oobCode format: /password/reset?oobCode=xxx
          {
            path: 'password/reset',
            element: (
              <LazyWrapper>
                <PasswordResetPage />
              </LazyWrapper>
            ),
          },
          // Email verification - Firebase oobCode format: /verify?oobCode=xxx&mode=verifyEmail
          {
            path: 'verify',
            element: (
              <LazyWrapper>
                <VerifyEmailPage />
              </LazyWrapper>
            ),
          },
          // Magic link sign-in
          {
            path: 'sign-email-link',
            element: (
              <LazyWrapper>
                <MagicLinkSignInPage />
              </LazyWrapper>
            ),
          },
          // Team invite acceptance
          {
            path: 'join',
            element: (
              <LazyWrapper>
                <JoinTeamPage />
              </LazyWrapper>
            ),
          },
          // SSO (scio1: /sso)
          {
            path: 'sso',
            element: (
              <LazyWrapper>
                <SsoLoginPage />
              </LazyWrapper>
            ),
          },
          // SSO Login (scio1: /auth/sso-login - this one has /auth/ prefix in scio1)
          {
            path: 'auth/sso-login',
            element: (
              <LazyWrapper>
                <SsoLoginPage />
              </LazyWrapper>
            ),
          },

          // === App routes (protected) — paths match scio1 exactly ===
          {
            path: 'app',
            element: <ProtectedRoute />,
            children: [
              // Main content pages
              {
                path: 'screenManagement',
                element: useNativeScreens ? (
                  <LazyWrapper>
                    <ScreensPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'assetManagement',
                element: useNativeAssets ? (
                  <LazyWrapper>
                    <AssetsPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'playlistManagement',
                element: useNativePlaylists ? (
                  <LazyWrapper>
                    <PlaylistsPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'scheduleManagement',
                element: useNativeSchedules ? (
                  <LazyWrapper>
                    <SchedulesPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'templateManagement',
                element: useNativeTemplates ? (
                  <RestrictedRoute>
                    <LazyWrapper>
                      <TemplatesPage />
                    </LazyWrapper>
                  </RestrictedRoute>
                ) : (
                  <RestrictedRoute>
                    <Scio1Frame />
                  </RestrictedRoute>
                ),
              },
              {
                path: 'engageManagement',
                element: useNativeEngage ? (
                  <LazyWrapper>
                    <EngagePage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'optidev',
                element: (
                  <LazyWrapper>
                    <OptiDevPage />
                  </LazyWrapper>
                ),
              },
              {
                path: 'dev/smallapp-playground',
                element: (
                  <AdminRoute>
                    <LazyWrapper>
                      <ManifestPlaygroundPage />
                    </LazyWrapper>
                  </AdminRoute>
                ),
              },
              {
                path: 'crm-management',
                element: useNativeEngage ? (
                  <LazyWrapper>
                    <EngageHubPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },
              {
                path: 'designManagement',
                element: (
                  <DesignerPermissionGate>
                    {useNativeDesigner ? (
                      <LazyWrapper>
                        <DesignerPage />
                      </LazyWrapper>
                    ) : (
                      <Scio1Frame />
                    )}
                  </DesignerPermissionGate>
                ),
              },
              // /app/designEditor → redirect to /app/designManagement (scio1 public editor is at /designEditor, not /app/)
              {
                path: 'designEditor',
                element: <Navigate to="/app/designManagement" replace />,
              },
              {
                path: 'feeds',
                element: useNativeFeeds ? (
                  <LazyWrapper>
                    <FeedsPageLazy />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },

              // Analytics
              {
                path: 'playbackReport',
                element: (
                  <AdminRoute>
                    <LazyWrapper>
                      <ProofOfPlayPage />
                    </LazyWrapper>
                  </AdminRoute>
                ),
              },
              {
                path: 'ai-analytics',
                element: (
                  <AdminRoute>
                    <LazyWrapper>
                      <AiAnalyticsPage />
                    </LazyWrapper>
                  </AdminRoute>
                ),
              },
              { path: 'proofofplay', element: <Scio1Frame /> },
              {
                path: 'security-audit-logging',
                element: useNativeAuditLog ? (
                  <LazyWrapper>
                    <AuditLogPage />
                  </LazyWrapper>
                ) : (
                  <Scio1Frame />
                ),
              },

              // === Settings — SettingsLayout handles native (Outlet) + iframe (Scio1Frame) ===
              {
                path: 's',
                element: <SettingsLayout />,
                children: [
                  // Native pages (render via Outlet when feature-flagged; null = iframe in SettingsLayout)
                  {
                    path: 'personal-profile',
                    element: useNativeProfile ? (
                      <LazyWrapper>
                        <PersonalProfilePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'company-profile',
                    element: useNativeCompanyProfile ? (
                      <LazyWrapper>
                        <CompanyProfilePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'external-coms',
                    element: useNativeExternalComs ? (
                      <LazyWrapper>
                        <ExternalComsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'preference-settings',
                    element: useNativePreferences ? (
                      <LazyWrapper>
                        <PreferencesPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'apikeys',
                    element: useNativeApiKeys ? (
                      <LazyWrapper>
                        <ApiKeysPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'feeds-admin',
                    element: useNativeFeedsAdmin ? (
                      <LazyWrapper>
                        <FeedsAdminPageLazy />
                      </LazyWrapper>
                    ) : null,
                  },
                  // Iframe-only settings pages
                  { path: 'account-members', element: null },
                  { path: 'subscription-plan', element: null },
                  { path: 'payment', element: null },
                  { path: 'payment-history', element: null },
                  { path: 'order-device', element: null },
                  { path: 'ordered-device', element: null },
                  { path: 'credentials', element: null },
                  {
                    path: 'operational-schedule',
                    element: useNativeOpsSchedule ? (
                      <LazyWrapper>
                        <OperationalSchedulePage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'approval-list', element: null },
                  { path: 'sub-account-billing', element: null },
                  { path: 'coupons', element: null },
                  {
                    path: 'proof-of-play-settings',
                    element: useNativePopSettings ? (
                      <LazyWrapper>
                        <ProofOfPlaySettingsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'branding-settings', element: null },
                  {
                    path: 'monitoring-alert-rules',
                    element: useNativeMonitoringAlertRules ? (
                      <LazyWrapper>
                        <MonitoringAlertRulesPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'provisioning-templates', element: null },
                  { path: 'saml-settings', element: null },
                  {
                    path: 'integrations',
                    element: useNativeIntegrations ? (
                      <LazyWrapper>
                        <IntegrationsPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'share-links-admin',
                    element: useNativeShareLinksAdmin ? (
                      <LazyWrapper>
                        <ShareLinksAdminPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  {
                    path: 'datasources-admin',
                    element: useNativeDataSources ? (
                      <LazyWrapper>
                        <DataSourcesPage />
                      </LazyWrapper>
                    ) : null,
                  },
                  { path: 'social', element: null },
                  { path: 'functional-locations', element: null },
                  { path: 'confirm-linked-account', element: null },

                  // scio1-only slugs — redirected by SettingsLayout via SETTINGS_REDIRECTS map
                  // (routes still needed so React Router matches them instead of falling through)
                  { path: 'android-devices', element: null },
                  { path: 'free-devices', element: null },
                  { path: 'payment-thankyou', element: null },
                  { path: 'payment-intent', element: null },
                  { path: 'subscription-thankyou', element: null },
                  { path: 'subscription-init-thankyou', element: null },
                  { path: 'proof-of-play-redirect', element: null },
                  { path: 'request-apis', element: null },
                  // Default settings page
                  { index: true, element: <Navigate to="personal-profile" replace /> },
                ],
              },

              ...(devMod?.devAppRoutes ?? []),

              // Catch-all for unmapped app routes
              { path: '*', element: <Scio1Frame /> },
            ],
          },

          // Catch-all redirect
          { path: '*', element: <Navigate to="/app/screenManagement" replace /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
